import type { CatalogItemImageInput } from "./catalog-item-image";
import type { CatalogEiTier } from "./catalog-item-badges";

// The details page's view of one item, and the display rules the page draws it
// by.
//
// **Declared here rather than imported from the wire face**, for the reason
// `@/lib/catalog-item` states at length: the client graph may never import
// `@/server/*`, type-only imports included. `catalog-wire-drift.unit.test.ts`
// is what keeps this shape and the wire's from parting company.
//
// It is a *second* item type beside `CatalogListItem` rather than an extension
// of it, because the two surfaces read different fields: the list needs a hero
// image and a description column, the details table needs material, prop id and
// three dimensions. A single union of both would let a list-only field be read
// here — from a payload that never carries it — and still typecheck.

/** A lookup row as the details table renders it. */
export interface CatalogDetailLookupRef {
  id: number;
  name: string;
}

/** A category ref, which always carries its slug on this wire. */
export interface CatalogDetailCategoryRef extends CatalogDetailLookupRef {
  slug: string;
}

export interface CatalogDetailItem {
  id: number;
  name: string;
  manufacturer: CatalogDetailLookupRef;
  material?: CatalogDetailLookupRef | null;
  category?: CatalogDetailCategoryRef | null;
  finish?: CatalogDetailLookupRef | null;
  modelNumber?: string | null;
  jdeItemNumber?: string | null;
  propId?: string | null;
  /** Dimensions travel as strings — decimals that must not round-trip a float. */
  dimensionWidth?: string | null;
  dimensionHeight?: string | null;
  dimensionDepth?: string | null;
  /** The manufacturer's own site, behind the View Manufacturer Site button. */
  url?: string | null;
  /** Sanitised HTML, rendered as markup by `HtmlBox` and nothing else. */
  description?: string | null;
  /**
   * How many specifications use the item — Item usage, live (ADR-0013).
   *
   * Optional on purpose: absent is "we do not know", which is a *different*
   * answer from zero and the one `deleteBlock` refuses on.
   */
  usageCount?: number;
  isApproved: boolean;
  isCore: boolean;
  isNotRecommended: boolean;
  eiTiers?: readonly CatalogEiTier[];
  /** Python's derived EI flag, which the page's EI mark trusts on its own. */
  hasEiTiers?: boolean;
  /**
   * The item's hero image, or absent/`null` when it has none.
   *
   * **Read by the Add Variant dialog's preview card only** (#254), not by the
   * details page itself — that page's gallery is the images slice's (#303) and
   * its slot stays a placeholder until then. The distinction is the ticket's own:
   * #254 asks the dialog's left column for "hero image (height 250, `?w=256`,
   * empty-image fallback)" and excludes exactly one image surface by name, the
   * dialog's *uploader* section.
   *
   * Server-resolved, like the list summary's — lowest `orderIndex`, tiebreak
   * `id` (ADR-0005 decision 9) — and the same field on the same
   * `itemSummaryWire`, so the group's members carry theirs too and the preview's
   * tile strip needs no second read.
   */
  heroImage?: CatalogItemImageInput | null;
}

/**
 * The Unspecified manufacturer, which may not take variants.
 *
 * `UnspecifiedManufacturerId = 1` (`timmy/src/services/common.ts:195`), and it
 * is the same row after the migration: ADR-0004 rules out remapping ids
 * anywhere in the pipeline, so the donor's constant carries over unchanged.
 */
export const UNSPECIFIED_MANUFACTURER_ID = 1;

export const ADD_VARIANT_BLOCKED_TOOLTIP =
  "Adding variants to unspecified manufacturer items is not currently supported.";

/**
 * Why Add Variant is refused for an item with no category — **not the donor's
 * wording, because the donor has no such refusal.**
 *
 * An owner ruling (#254 decision menu, D3 = c). Category is required on the
 * form and locked to the parent's value, so a parent with no category opens a
 * dialog that can never be submitted: a required field, empty, that the typist
 * cannot edit. modsy has exactly that dead end. Refusing in the menu instead
 * keeps the affordance honest and explains itself, and it reuses the donor's own
 * idiom — a greyed entry with a tooltip saying why — rather than inventing a
 * new one.
 */
export const ADD_VARIANT_NO_CATEGORY_TOOLTIP =
  "Adding variants requires the item to have a category.";

/**
 * A dimension at exactly two decimal places, or nothing to print.
 *
 * The donor's `item.dimensionWidth?.toFixed(2)` on a number
 * (`Details.tsx:38-40`); here the value arrives as a string, so the parse is
 * this side's addition and the formatting is the donor's. A value that does not
 * parse has no two-decimal reading, so it takes the same arm as an absent one
 * and its row disappears — which is what the donor does with a `null` too.
 */
export function formatDimension(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
}

/** One row of the details table: a label, and the text beside it. */
export interface CatalogDetailRow {
  label: string;
  value: string;
}

/**
 * The facts the details table draws, flat and lookup-free.
 *
 * **The row order has one home, and two payload shapes reach it.** The authed
 * detail wire nests its lookups (`manufacturer.name`); the public wire (#308)
 * sends names only. Writing the row list twice would be two places for
 * `Details.tsx:31-40`'s ordering to live, and the second one is the one that
 * drifts — so the ordering takes this shape and each payload adapts onto it.
 *
 * Every field but the manufacturer is optional, because every one of them is
 * optional on at least one of the two wires.
 *
 * **No `propId`.** The donor's table declares a Prop Id row and never draws it:
 * it reads `item?.prop_id` off a payload whose key is camelCase `propId`
 * (Falcon `models.py:71`), and Falcon fills that from ItemLib's *public* item,
 * which carries no `prop_id` at all (`router.py:55, 73`), so the value is null
 * on the authed page too. The authed wire still carries `propId` — the item
 * edit page and Request Item edit it, which are other screens — but no surface
 * of this table can draw it (#718 C1, folded from #713; the public side was
 * ruled by spec #270).
 */
export interface CatalogDetailFacts {
  manufacturerName: string;
  materialName?: string | null;
  categoryName?: string | null;
  modelNumber?: string | null;
  jdeItemNumber?: string | null;
  finishName?: string | null;
  dimensionWidth?: string | null;
  dimensionHeight?: string | null;
  dimensionDepth?: string | null;
}

/**
 * The details table's rows, in the donor's order, minus the ones this item has
 * nothing to say for.
 *
 * **Omitting the row is the details page's rule and the opposite of the card's**
 * (`@/lib/catalog-item`'s `fieldText`, which prints `N/A`). The donor's
 * `DetailTableRow` returns `null` on a falsy value, so the table is as short as
 * the item is sparse — which reads correctly here, where the rows are a
 * paragraph about one item rather than a column to scan twenty cards down.
 *
 * **The dimensions are formatted before the falsy test, not after**, which is
 * the donor's own ordering (`Details.tsx:38-40` passes `toFixed(2)`'s result to
 * the row): a dimension of exactly zero becomes the truthy `"0.00"` and keeps
 * its row, where testing the raw value first would drop it.
 *
 * The labels are the donor's strings, `'Item Number'` included: the ticket
 * writes it "Item Number (JDE)", but `Details.tsx:35` does not, and the
 * rendered string is the spec.
 */
export function detailRowsFromFacts(facts: CatalogDetailFacts): CatalogDetailRow[] {
  const candidates: { label: string; value: string | null | undefined }[] = [
    { label: "Manufacturer", value: facts.manufacturerName },
    { label: "Material", value: facts.materialName },
    { label: "Item Category", value: facts.categoryName },
    { label: "Model Number", value: facts.modelNumber },
    { label: "Item Number", value: facts.jdeItemNumber },
    { label: "Color/Finish", value: facts.finishName },
    { label: "Width (inches)", value: formatDimension(facts.dimensionWidth) },
    { label: "Height (inches)", value: formatDimension(facts.dimensionHeight) },
    { label: "Depth (inches)", value: formatDimension(facts.dimensionDepth) },
  ];

  return candidates.flatMap(({ label, value }) => {
    const text = value?.trim();
    return text ? [{ label, value: text }] : [];
  });
}

/** The same rows, from the authed detail payload's nested lookups. */
export function detailRows(item: CatalogDetailItem): CatalogDetailRow[] {
  return detailRowsFromFacts({
    manufacturerName: item.manufacturer.name,
    materialName: item.material?.name,
    categoryName: item.category?.name,
    modelNumber: item.modelNumber,
    jdeItemNumber: item.jdeItemNumber,
    finishName: item.finish?.name,
    dimensionWidth: item.dimensionWidth,
    dimensionHeight: item.dimensionHeight,
    dimensionDepth: item.dimensionDepth,
  });
}

/**
 * Whether any specification uses the item — the breakdown lists its rows, or
 * opens onto "Not used in any specifications" instead.
 *
 * The donor's own predicate, `coreSpecCnt > 0 || totalSpecCnt > 0`
 * (`SpecUsage.tsx:66`): the core count rides the detail read beside the item
 * (`coreUsageCount`), since the list's summary cannot carry it. It no longer
 * gates the chevron, which the donor draws whatever the usage since timmy
 * `c701607d` (#553 D1).
 */
export function hasSpecUsage(item: CatalogDetailItem, coreUsageCount: number): boolean {
  return coreUsageCount > 0 || (item.usageCount ?? 0) > 0;
}

/**
 * Why Add Variant is disabled, or `null` when it is not.
 *
 * A reason string rather than a boolean because the disabled arm always carries
 * a tooltip in the donor: a caller that got `true` would still have to find the
 * wording, and the second place that wording lived would be the one that drifts.
 *
 * **Two reasons, and only the first is the donor's.** The manufacturer gate is
 * `ActionMenu.tsx`'s. The category gate is an owner-ruled parity break (#254's
 * D3 = c): the donor lets the dialog open and dead-end on a locked, empty,
 * required Category, and this refuses in the menu instead. Order matters only
 * for an item that fails both, where the manufacturer reason is the one modsy
 * would also have given.
 *
 * Asks only the two fields it reads, so the Item Search island's menu can ask it
 * of a list row (#798), whose category ref carries no slug.
 */
export function addVariantBlock(item: {
  manufacturer: { id: number };
  category?: { id: number } | null;
}): string | null {
  if (item.manufacturer.id === UNSPECIFIED_MANUFACTURER_ID) return ADD_VARIANT_BLOCKED_TOOLTIP;
  if (item.category == null) return ADD_VARIANT_NO_CATEGORY_TOOLTIP;
  return null;
}

/**
 * Why Delete is disabled, or `null` when it is not.
 *
 * **Unknown usage counts as used.** `itemSpecCount = specificationCount ?? 1`
 * (`ActionMenu.tsx:122`) — the donor errs toward refusing, so an item whose
 * usage never arrived cannot be deleted by accident. Python refuses such a
 * delete anyway (`delete-refused:item`), which is the enforcement; this is the
 * affordance, and it must not be the more permissive of the two.
 *
 * The wording, count included, is the donor's — "1 specifications" and all: it
 * does not pluralise, and inventing the singular here would be a visible
 * divergence from the screen this is a port of.
 *
 * **Takes the count, not the item** (#457 R8). The catalog list's own actions
 * menu asks the same question of a `CatalogListItem`, which is a different type
 * carrying the same field — and the ruling was that the list reuse this wording
 * rather than retype it. Asking for the one number the rule reads is what lets
 * both item shapes ask it. The `?? 1` stays for the details page's sake:
 * `usageCount` is optional there and required on a list row, so the hedge has
 * something to hedge on one caller and nothing to do on the other.
 */
export function deleteBlock(usageCount?: number): string | null {
  const count = usageCount ?? 1;
  return count > 0
    ? `This item cannot be deleted because it is associated with ${count} specifications.`
    : null;
}
